import { transFormOccupancy } from "../common/common.js";
import { ARM_TERM_OPTIONS, restoreArmTerm } from "../../armTerms";
import { DEFAULT_TARGET_PRICE, LOAN_PROGRAM_OPTIONS_BY_LIEN } from "../../config";
import { ITIN_CITIZENSHIP, searchTargetOf } from "../../pricingRequest";
import { zip5 } from "../../zipCounty";

const LOAN_PROGRAMS = new Set(
  Object.values(LOAN_PROGRAM_OPTIONS_BY_LIEN).flat().map(({ value }) => value),
);
const LOAN_TERMS = new Set([
  "fixed40", "fixed30", "fixed25", "fixed20", "fixed15", "fixed10",
  ...ARM_TERM_OPTIONS.map(({ value }) => value),
]);
// Citizenship in a link: the form's values, or the calculator's camelCase.
const CITIZENSHIP_FROM_QUERY = {
  US_CITIZENS: "US_CITIZENS",
  USCITIZEN: "US_CITIZENS",
  PERMANENT_RESIDENT_ALIEN: "PERMANENT_RESIDENT_ALIEN",
  PERMANENTRESIDENT: "PERMANENT_RESIDENT_ALIEN",
  NON_PERMANENT_RESIDENT_ALIEN: "NON_PERMANENT_RESIDENT_ALIEN",
  NONPERMANENTRESIDENT: "NON_PERMANENT_RESIDENT_ALIEN",
  NON_RESIDENT_ALIEN: "NON_RESIDENT_ALIEN",
  FOREIGNNATIONAL: "NON_RESIDENT_ALIEN",
  ITIN: ITIN_CITIZENSHIP,
};

const QUERY_KEYS = [
  "loanProgram",
  "loanTerms",
  "loanTerm",
  "zip",
  "citizenship",
  "fico",
  "loanAmount",
  "state",
  "county",
  "housePrice",
  "purpose",
  "occupancy",
  "monthlyIncome",
  "monthlyDebt",
  "reserves",
  "propertiesFinanced",
];

const STATE_ABBREV_TO_FULL = {
  AL: "ALABAMA",
  AK: "ALASKA",
  AZ: "ARIZONA",
  AR: "ARKANSAS",
  CA: "CALIFORNIA",
  CO: "COLORADO",
  CT: "CONNECTICUT",
  DE: "DELAWARE",
  FL: "FLORIDA",
  GA: "GEORGIA",
  HI: "HAWAII",
  ID: "IDAHO",
  IL: "ILLINOIS",
  IN: "INDIANA",
  IA: "IOWA",
  KS: "KANSAS",
  KY: "KENTUCKY",
  LA: "LOUISIANA",
  ME: "MAINE",
  MD: "MARYLAND",
  MA: "MASSACHUSETTS",
  MI: "MICHIGAN",
  MN: "MINNESOTA",
  MS: "MISSISSIPPI",
  MO: "MISSOURI",
  MT: "MONTANA",
  NE: "NEBRASKA",
  NV: "NEVADA",
  NH: "NEW HAMPSHIRE",
  NJ: "NEW JERSEY",
  NM: "NEW MEXICO",
  NY: "NEW YORK",
  NC: "NORTH CAROLINA",
  ND: "NORTH DAKOTA",
  OH: "OHIO",
  OK: "OKLAHOMA",
  OR: "OREGON",
  PA: "PENNSYLVANIA",
  RI: "RHODE ISLAND",
  SC: "SOUTH CAROLINA",
  SD: "SOUTH DAKOTA",
  TN: "TENNESSEE",
  TX: "TEXAS",
  UT: "UTAH",
  VT: "VERMONT",
  VA: "VIRGINIA",
  WA: "WASHINGTON",
  WV: "WEST VIRGINIA",
  WI: "WISCONSIN",
  WY: "WYOMING",
};

const VALID_PURPOSES = new Set(["Purchase", "RateTermRefi", "CashOut"]);

function queryHasKey(query, key) {
  if (!query.has(key)) return false;
  const value = query.get(key);
  return value != null && value !== "";
}

function parseIntOrNull(value, { min, max } = {}) {
  if (value == null || value === "") return null;
  const n = Number.parseInt(String(value).replace(/,/g, ""), 10);
  if (!Number.isFinite(n)) return null;
  if (min != null && n < min) return null;
  if (max != null && n > max) return null;
  return n;
}

function parseNumberOrNull(value, { min, max } = {}) {
  if (value == null || value === "") return null;
  const n = Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(n)) return null;
  if (min != null && n < min) return null;
  if (max != null && n > max) return null;
  return n;
}

export function normalizeState(raw) {
  if (!raw) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  const upper = trimmed.toUpperCase();
  if (upper.length === 2 && STATE_ABBREV_TO_FULL[upper]) {
    return STATE_ABBREV_TO_FULL[upper];
  }
  return upper;
}

export function normalizeCounty(raw) {
  if (!raw) return null;
  const county = String(raw).trim().toUpperCase();
  if (!county) return null;
  if (/\b(COUNTY|PARISH)$/i.test(county)) {
    return county;
  }
  return `${county} COUNTY`;
}

export function normalizePurpose(raw) {
  if (!raw) return null;
  const purpose = String(raw).trim();
  if (!VALID_PURPOSES.has(purpose)) return null;
  if (purpose === "CashOut") return "CashOutRefi";
  return purpose;
}

export function normalizeOccupancy(raw) {
  if (!raw) return null;
  const occupancy = String(raw).trim().toUpperCase();
  if (!["PRIMARY", "SECOND_HOME", "INVESTMENT"].includes(occupancy)) {
    return null;
  }
  return transFormOccupancy(occupancy);
}

export function normalizeLoanTerms(raw) {
  if (!raw) return [];
  const terms = String(raw).split(",")
    .map((term) => restoreArmTerm(term.trim().toLowerCase()))
    .filter((term) => LOAN_TERMS.has(term));
  return [...new Set(terms)];
}

export function normalizeCitizenship(raw) {
  if (!raw) return null;
  return CITIZENSHIP_FROM_QUERY[String(raw).trim().toUpperCase()] || null;
}

/**
 * A saved search (lenders.search_history stores the form body) as the form
 * and the request expect it today (plan D17):
 * - a search by rate stays one (the target rate came back on 11-04), with
 *   the rate in targetRate; one with no usable rate becomes a search by price
 *   at the default target (pricingRequest.searchTargetOf);
 * - the target price components and buydown are dropped;
 * - the old ITIN box becomes Citizenship = ITIN for Non-QM; elsewhere ITIN
 *   is not offered and the borrower stays a non-permanent resident;
 * - one loanTerm becomes the Loan terms list, older ARM values as N/6.
 * Income is left as saved: a search saved without income stays without.
 */
export function restoreSavedSearch(params) {
  const body = { ...(params || {}) };
  const target = searchTargetOf(body);
  body.searchBy = target.by;
  if (target.by === "rate") {
    body.targetRate = target.value;
    // The price box keeps its default for when the officer switches back.
    body.targetPrice = DEFAULT_TARGET_PRICE;
  } else {
    body.targetPrice = target.value;
    delete body.targetRate;
  }
  delete body.targetPriceType;
  delete body.buydown;
  if (body.itin && body.citizenship !== ITIN_CITIZENSHIP) {
    body.citizenship = body.loanProgram === "nonqm" ? ITIN_CITIZENSHIP : "NON_PERMANENT_RESIDENT_ALIEN";
  }
  delete body.itin;
  const savedTerms = Array.isArray(body.selectedLoanTerms) && body.selectedLoanTerms.length
    ? body.selectedLoanTerms
    : [body.loanTerm];
  const loanTerms = savedTerms.filter(Boolean).map(restoreArmTerm);
  if (loanTerms.length) {
    body.selectedLoanTerms = loanTerms;
    body.loanTerm = loanTerms[0];
  }
  return body;
}

export function matchCountyInLoanLimits(county, state, loanLimits) {
  if (!county || !state || !loanLimits?.[state]) return county;
  const keys = Object.keys(loanLimits[state]);
  if (keys.includes(county)) return county;
  const base = county.replace(/\s+(COUNTY|PARISH)$/i, "").trim();
  const match = keys.find(
    (key) => key.replace(/\s+(COUNTY|PARISH)$/i, "").trim() === base
  );
  return match || county;
}

function stripUrlQuery() {
  if (typeof window === "undefined" || !window.location.search) return;
  const { pathname, hash } = window.location;
  window.history.replaceState(window.history.state, "", pathname + hash);
}

function hasRecognizedQueryParams(query) {
  return QUERY_KEYS.some((key) => queryHasKey(query, key));
}

/**
 * 深链 query 解析：仅返回 URL 中出现的字段（稀疏对象），键名与 URL 一致。
 * 无相关 query 时返回 null，调用方走表单原有默认值。
 * 读取后立即清除 URL query。
 */
export function parseSearchFieldsFromUrlQuery() {
  if (typeof window === "undefined") return null;

  const query = new URLSearchParams(window.location.search);
  if (!hasRecognizedQueryParams(query)) return null;

  const fields = {};

  if (queryHasKey(query, "fico")) {
    const fico = parseIntOrNull(query.get("fico"), { min: 300, max: 850 });
    if (fico != null) fields.fico = fico;
  }

  if (queryHasKey(query, "loanAmount")) {
    const loanAmount = parseIntOrNull(query.get("loanAmount"), {
      min: 1,
      max: 100_000_000,
    });
    if (loanAmount != null) fields.loanAmount = loanAmount;
  }

  if (queryHasKey(query, "state")) {
    const state = normalizeState(query.get("state"));
    if (state) fields.state = state;
  }

  if (queryHasKey(query, "county")) {
    const county = normalizeCounty(query.get("county"));
    if (county) fields.county = county;
  }

  if (queryHasKey(query, "housePrice")) {
    const housePrice = parseIntOrNull(query.get("housePrice"), {
      min: 1,
      max: 100_000_000,
    });
    if (housePrice != null) fields.housePrice = housePrice;
  }

  if (queryHasKey(query, "purpose")) {
    const purpose = normalizePurpose(query.get("purpose"));
    if (purpose) fields.purpose = purpose;
  }

  if (queryHasKey(query, "occupancy")) {
    const occupancy = normalizeOccupancy(query.get("occupancy"));
    if (occupancy) fields.occupancy = occupancy;
  }

  if (queryHasKey(query, "monthlyIncome")) {
    const monthlyIncome = parseIntOrNull(query.get("monthlyIncome"), {
      min: 0,
      max: 10_000_000,
    });
    if (monthlyIncome != null) fields.monthlyIncome = monthlyIncome;
  }

  if (queryHasKey(query, "monthlyDebt")) {
    const monthlyDebt = parseIntOrNull(query.get("monthlyDebt"), {
      min: 0,
      max: 10_000_000,
    });
    if (monthlyDebt != null) fields.monthlyDebt = monthlyDebt;
  }

  if (queryHasKey(query, "reserves")) {
    const reserves = parseNumberOrNull(query.get("reserves"), { min: 0, max: 1200 });
    if (reserves != null) fields.reserves = reserves;
  }

  if (queryHasKey(query, "loanProgram")) {
    const loanProgram = String(query.get("loanProgram")).trim();
    if (LOAN_PROGRAMS.has(loanProgram)) fields.loanProgram = loanProgram;
  }

  // loanTerms=fixed30,arm56 (or one loanTerm). Only known terms are kept.
  if (queryHasKey(query, "loanTerms") || queryHasKey(query, "loanTerm")) {
    const loanTerms = normalizeLoanTerms(query.get("loanTerms") || query.get("loanTerm"));
    if (loanTerms.length) fields.loanTerms = loanTerms;
  }

  if (queryHasKey(query, "zip")) {
    const zip = zip5(query.get("zip"));
    if (zip) fields.zip = zip;
  }

  if (queryHasKey(query, "citizenship")) {
    const citizenship = normalizeCitizenship(query.get("citizenship"));
    if (citizenship) fields.citizenship = citizenship;
  }

  if (queryHasKey(query, "propertiesFinanced")) {
    const propertiesFinanced = parseIntOrNull(query.get("propertiesFinanced"), {
      min: 0,
      max: 100,
    });
    if (propertiesFinanced != null) fields.propertiesFinanced = propertiesFinanced;
  }

  stripUrlQuery();
  return { fromDeepLink: true, ...fields };
}
